import apiClient from './api';

export interface ScormUploadResponse {
  package_id: string;
  status: string;
}

export interface ScormPackageStatus {
  package_id: string;
  status: 'processing' | 'ready' | 'failed';
  scorm_version?: string;
  original_filename?: string;
  course_ids: string[];
  error_message?: string;
}

export interface ScormCmiPayload {
  lesson_status?: string;
  score_raw?: number;
  score_min?: number;
  score_max?: number;
  session_time?: string;
  lesson_location?: string;
  suspend_data?: string;
  exit?: string;
  entry?: string;
  objectives?: { id: string; score_raw?: number; status?: string }[];
  interactions?: { id: string; type?: string; student_response?: string; result?: string; latency?: string }[];
}

export interface ScormCommitRequest {
  course_id: string;
  module_id: string;
  content_item_id: string;
  scorm_package_id?: string;
  resource_identifier?: string;
  mastery_score?: number;
  attempt_session?: string;   // new SCO launch => new attempt
  finished?: boolean;         // true when this commit came from LMSFinish
  trigger?: string;           // which call caused the commit (lesson_status verdict vs nav/auto)
  cmi: ScormCmiPayload;
}

/**
 * Result of a runtime commit. The backend decides completion/pass (it compares the score
 * against the mastery score when the SCO leaves lesson_status non-terminal), so the player
 * should trust `completed`/`passed` rather than the raw lesson_status string.
 */
export interface ScormCommitResult {
  ok: boolean;
  completed?: boolean;
  passed?: boolean;
  tracking_updated?: boolean;
  tracking_error?: string;
}

export interface ScormResumeState {
  lesson_location?: string;
  suspend_data?: string;
  lesson_status?: string;
  entry: string;
}

export interface ScormAttempt {
  attempt_number: number;
  lesson_status?: string;
  score_raw?: number;
  score_percentage?: number;
  finished?: boolean;
  completed_at?: string;
}

/** Learner's assessment record for a SCO (null for non-quiz content). */
export interface ScormAssessment {
  content_item_id: string;
  attempt_count: number;
  attempts: ScormAttempt[];
  lesson_status?: string;
  score_raw?: number;
  score_percentage?: number;
  passed?: boolean;
  completed_at?: string;
  max_attempts?: number;   // course attempt cap (1 = single); 0/undefined = unlimited
  locked?: boolean;        // true when the learner has used all attempts
}

export interface ScormPackageListItem {
  package_id: string;
  status: 'processing' | 'ready' | 'failed';
  scorm_version?: string;
  original_filename?: string;
  original_zip_url?: string;
  createdAt?: string;
  updatedAt?: string;
  error_message?: string;
  courses: { id: string; title: string; stage?: string }[];
}

export interface ScormPackageListResult {
  packages: ScormPackageListItem[];
  total_count: number;
}

export interface ScormMetrics {
  total: number;
  ready: number;
  failed: number;
  processing: number;
}

/**
 * Client for the SCORM backend endpoints (SRV ScormController at /scorm/*).
 * The API response is wrapped in a ResultEntity { data, ... }; we unwrap .data.
 */
class ScormService {
  /**
   * Step 1: stream-upload the .zip to S3 staging (no buffering server-side, so 2 GB is fine).
   * Returns the staging key to pass to ingest(). onProgress reports upload % (0–100).
   */
  async stageUpload(
    file: File,
    onProgress?: (percent: number) => void,
  ): Promise<{ s3_key: string; s3_url: string; original_filename: string }> {
    const form = new FormData();
    form.append('file', file);
    const res = await apiClient.post('/scorm/packages/upload', form, {
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
      },
    });
    return res.data?.data ?? res.data;
  }

  /** Step 2: kick off background extraction of an already-staged zip. Returns a package_id to poll. */
  async ingest(
    s3Key: string,
    originalFilename: string,
    maxAttempts?: number,
    s3Url?: string,
  ): Promise<ScormUploadResponse> {
    const res = await apiClient.post('/scorm/packages/ingest', {
      s3_key: s3Key,
      s3_url: s3Url,
      original_filename: originalFilename,
      max_attempts: maxAttempts && maxAttempts >= 1 ? maxAttempts : undefined,
    });
    return res.data?.data ?? res.data;
  }

  /**
   * Upload a SCORM .zip; returns a package_id to poll. Streams the zip to S3 (step 1) then
   * triggers background ingest (step 2) — supports large packages (~2 GB) without OOM.
   * `maxAttempts` caps quiz attempts on the imported course (1 = single; undefined/0 = unlimited).
   * `onProgress` reports the upload percentage (0–100).
   */
  async uploadPackage(
    file: File,
    maxAttempts?: number,
    onProgress?: (percent: number) => void,
  ): Promise<ScormUploadResponse> {
    const staged = await this.stageUpload(file, onProgress);
    return this.ingest(staged.s3_key, staged.original_filename, maxAttempts, staged.s3_url);
  }

  /** List SCORM packages (paginated, newest first) for the imports panel. */
  async listPackages(limit = 6, skip = 0): Promise<ScormPackageListResult> {
    const res = await apiClient.get('/scorm/packages', { params: { limit, skip } });
    const data = res.data?.data ?? res.data;
    // Tolerate the legacy array shape in case of a stale backend.
    if (Array.isArray(data)) return { packages: data, total_count: data.length };
    return { packages: data?.packages ?? [], total_count: data?.total_count ?? 0 };
  }

  /** Import metrics for the dashboard cards (total / ready / failed / processing). */
  async getMetrics(): Promise<ScormMetrics> {
    const res = await apiClient.get('/scorm/packages/metrics');
    return res.data?.data ?? res.data ?? { total: 0, ready: 0, failed: 0, processing: 0 };
  }

  /** Poll the ingest status of a package. */
  async getPackageStatus(packageId: string): Promise<ScormPackageStatus> {
    const res = await apiClient.get(`/scorm/packages/${packageId}`);
    return res.data?.data ?? res.data;
  }

  /** Delete a package and its course(s). */
  async deletePackage(packageId: string): Promise<{ message: string }> {
    const res = await apiClient.delete(`/scorm/packages/${packageId}`);
    return res.data?.data ?? res.data;
  }

  /** Commit SCORM runtime CMI (score + completion). */
  async commit(request: ScormCommitRequest): Promise<ScormCommitResult> {
    const res = await apiClient.post('/scorm/runtime/commit', request);
    return res.data?.data ?? res.data;
  }

  /** Get saved resume state for a content item. */
  async getResume(contentItemId: string): Promise<ScormResumeState> {
    const res = await apiClient.get('/scorm/runtime/resume', {
      params: { content_item_id: contentItemId },
    });
    return res.data?.data ?? res.data;
  }

  /** Get the learner's assessment result (attempts + score) for a content item. Null = non-quiz. */
  async getAssessment(contentItemId: string): Promise<ScormAssessment | null> {
    const res = await apiClient.get('/scorm/runtime/assessment', {
      params: { content_item_id: contentItemId },
    });
    return res.data?.data ?? res.data ?? null;
  }
}

export const scormService = new ScormService();
